import React, { useCallback } from 'react';
import { BarChart2, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import LoanParametersSection from './LoanParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import EarlyRepaymentSection from './EarlyRepaymentSection';
import RefinancingSection from './RefinancingSection';
import AdditionalCostsSection from './AdditionalCostsSection';


// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета ипотеки
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  creditTypes,
  earlyRepaymentPeriods 
}) => {
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем процентную ставку автоматически при изменении типа кредита
      if (name === 'creditType') {
        const selectedCreditType = creditTypes.find(type => type.id === fieldValue);
        if (selectedCreditType) {
          const currentRate = parseFloatOrDefault(prev.interestRate, 0);
          const isRateDefault = creditTypes.some(ct => currentRate === ct.defaultRate);
          if (isRateDefault || !prev.interestRate || currentRate <= 0) {
            newState.interestRate = selectedCreditType.defaultRate.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeEarlyRepayment' && !checked) {
        newState.earlyRepaymentType = 'reduction';
        newState.earlyRepaymentAmount = '100000';
        newState.earlyRepaymentPeriod = 'monthly';
        newState.earlyRepaymentStartMonth = '12';
      }
      if (name === 'includeRefinancing' && !checked) {
        newState.refinancingMonth = '60';
        newState.refinancingRate = '6.0';
      }
      if (name === 'includeInsurance' && !checked) {
        newState.insuranceRate = '0.5';
      }
      if (name === 'includeOtherCosts' && !checked) {
        newState.otherCostsAmount = '10000';
      }

      // Корректировка первоначального взноса при изменении стоимости недвижимости
      if (name === 'propertyPrice') {
        const numPrice = parseFloatOrDefault(fieldValue, 0);
        const numInitial = parseFloatOrDefault(prev.initialPayment, 0);
        if (numPrice > 0 && numInitial >= numPrice) {
          newState.initialPayment = Math.round(numPrice * 0.2).toString();
        } else if (numPrice <= 0) {
          newState.initialPayment = '0';
        }
      }

      // Корректировка при изменении первоначального взноса
      if (name === 'initialPayment') {
        const numInitial = parseFloatOrDefault(fieldValue, 0);
        if (numInitial < 0) {
          newState.initialPayment = '0';
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [creditTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      propertyPrice, initialPayment, loanTerm, interestRate,
      includeEarlyRepayment, earlyRepaymentAmount, earlyRepaymentStartMonth, earlyRepaymentPeriod, earlyRepaymentType,
      includeRefinancing, refinancingMonth, refinancingRate,
      includeInsurance, insuranceRate,
      includeOtherCosts, otherCostsAmount, paymentType
    } = formData;

    // Парсинг числовых значений
    const numPropertyPrice = parseFloatOrDefault(propertyPrice, -1);
    const numInitialPayment = parseFloatOrDefault(initialPayment, -1);
    const numLoanTermYears = parseFloatOrDefault(loanTerm, -1);
    const numLoanTermMonths = Math.round(numLoanTermYears * 12);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);

    // Валидация базовых полей
    if (numPropertyPrice <= 0) newErrors.propertyPrice = 'Стоимость должна быть положительной.';
    if (numInitialPayment < 0) newErrors.initialPayment = 'Взнос не может быть отрицательным.';
    if (!newErrors.propertyPrice && !newErrors.initialPayment && numInitialPayment >= numPropertyPrice) {
      newErrors.initialPayment = 'Взнос должен быть меньше стоимости недвижимости.';
    }
    if (numLoanTermYears <= 0) newErrors.loanTerm = 'Срок должен быть положительным.';
    if (!newErrors.loanTerm && numLoanTermYears > 50) newErrors.loanTerm = 'Максимальный срок 50 лет.';
    if (!newErrors.loanTerm && numLoanTermMonths <= 0) newErrors.loanTerm = 'Срок должен быть не менее 1 месяца.';
    if (numInterestRate <= 0) newErrors.interestRate = 'Ставка должна быть положительной.';
    if (!newErrors.interestRate && numInterestRate > 100) newErrors.interestRate = 'Ставка не может быть более 100%.';

    // Валидация досрочного погашения
    if (includeEarlyRepayment) {
      const numEarlyRepaymentAmount = parseFloatOrDefault(earlyRepaymentAmount, -1);
      const numEarlyRepaymentStartMonth = parseIntOrDefault(earlyRepaymentStartMonth, -1);
      if (numEarlyRepaymentAmount <= 0) newErrors.earlyRepaymentAmount = 'Сумма должна быть положительной.';
      if (!isInteger(earlyRepaymentStartMonth) || numEarlyRepaymentStartMonth <= 0) {
        newErrors.earlyRepaymentStartMonth = 'Введите целый полож. номер месяца.';
      }
      if (!newErrors.loanTerm && !newErrors.earlyRepaymentStartMonth && numEarlyRepaymentStartMonth > numLoanTermMonths) {
        newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) не может быть позже срока (${numLoanTermMonths} мес.).`;
      }
      if (!earlyRepaymentPeriod) newErrors.earlyRepaymentPeriod = "Выберите периодичность.";
      if (!earlyRepaymentType) newErrors.earlyRepaymentType = "Выберите цель погашения.";
    }

    // Валидация рефинансирования
    if (includeRefinancing) {
      const numRefinancingMonth = parseIntOrDefault(refinancingMonth, -1);
      const numRefinancingRate = parseFloatOrDefault(refinancingRate, -1);
      if (!isInteger(refinancingMonth) || numRefinancingMonth <= 0) {
        newErrors.refinancingMonth = 'Введите целый полож. номер месяца.';
      }
      if (!newErrors.loanTerm && !newErrors.refinancingMonth && numRefinancingMonth >= numLoanTermMonths) {
        newErrors.refinancingMonth = `Месяц (${numRefinancingMonth}) должен быть меньше срока (${numLoanTermMonths} мес.).`;
      }
      if (numRefinancingRate <= 0) newErrors.refinancingRate = 'Ставка должна быть положительной.';
      if (!newErrors.refinancingRate && numRefinancingRate > 100) newErrors.refinancingRate = 'Ставка не может быть более 100%.';
    }

    // Валидация страхования
    if (includeInsurance) {
      const numInsuranceRate = parseFloatOrDefault(insuranceRate, -1);
      if (numInsuranceRate <= 0) newErrors.insuranceRate = 'Ставка страх. должна быть полож.';
      if (!newErrors.insuranceRate && numInsuranceRate > 20) newErrors.insuranceRate = 'Ставка страх. не > 20%.';
    }

    // Валидация других расходов
    if (includeOtherCosts) {
      const numOtherCostsAmount = parseFloatOrDefault(otherCostsAmount, -1);
      if (numOtherCostsAmount <= 0) newErrors.otherCostsAmount = 'Сумма расходов должна быть полож.';
    }

    // Валидация типа платежа
    if (!paymentType || !['annuity', 'differentiated'].includes(paymentType)) {
      newErrors.paymentType = "Выберите корректный тип платежа.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет аннуитетного платежа
  const calculateAnnuityPayment = (principal, termMonths, annualRate) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;

    if (monthlyRate === 0) {
      return principal / termMonths;
    }

    const factor = Math.pow(1 + monthlyRate, termMonths);
    if (!isFinite(factor) || factor === 1) {
      return principal / termMonths;
    }
    return principal * monthlyRate * factor / (factor - 1);
  };

  // Функция расчета графика платежей
  const calculatePaymentSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numPropertyPrice = parseFloatOrDefault(formData.propertyPrice);
        const numInitialPayment = parseFloatOrDefault(formData.initialPayment);
        const initialLoanAmount = numPropertyPrice - numInitialPayment;

        if (initialLoanAmount <= 0) {
          setErrors({ general: "Сумма кредита (Стоимость - Взнос) должна быть положительной." });
          setIsCalculating(false);
          return;
        }

        const initialLoanTermYears = parseFloatOrDefault(formData.loanTerm);
        const initialLoanTermMonths = Math.round(initialLoanTermYears * 12);
        const initialAnnualRate = parseFloatOrDefault(formData.interestRate);
        const paymentType = formData.paymentType;

        const includeEarlyRepayment = formData.includeEarlyRepayment;
        const earlyRepaymentType = formData.earlyRepaymentType;
        const earlyRepaymentAmount = includeEarlyRepayment ? parseFloatOrDefault(formData.earlyRepaymentAmount, 0) : 0;
        const earlyRepaymentPeriod = formData.earlyRepaymentPeriod;
        const earlyRepaymentStartMonth = includeEarlyRepayment ? parseIntOrDefault(formData.earlyRepaymentStartMonth, 1) : 1;

        const includeRefinancing = formData.includeRefinancing;
        const refinancingMonth = includeRefinancing ? parseIntOrDefault(formData.refinancingMonth, 0) : 0;
        const refinancingRate = includeRefinancing ? parseFloatOrDefault(formData.refinancingRate, 0) : 0;

        const includeInsurance = formData.includeInsurance;
        const annualInsuranceRatePercent = includeInsurance ? parseFloatOrDefault(formData.insuranceRate, 0) : 0;
        const includeOtherCosts = formData.includeOtherCosts;
        const otherCostsAmount = includeOtherCosts ? parseFloatOrDefault(formData.otherCostsAmount, 0) : 0;

        // --- Инициализируем переменные для расчета ---
        let currentLoanBalance = initialLoanAmount;
        let currentAnnualRate = initialAnnualRate;
        let remainingTermMonthsForCalc = initialLoanTermMonths;
        let totalPrincipalPaidScheduled = 0;
        let totalInterestPaid = 0;
        let totalInsurancePaid = 0;
        let totalEarlyRepaymentPaid = 0;
        const totalOtherCostsPaid = otherCostsAmount;

        const calculatedSchedule = [];
        let currentMonth = 0;
        const startDate = new Date();

        // Рассчитываем начальный аннуитетный платеж (если применимо)
        let fixedAnnuityPayment = (paymentType === 'annuity')
            ? calculateAnnuityPayment(initialLoanAmount, initialLoanTermMonths, initialAnnualRate)
            : 0;

        // Определяем частоту досрочного погашения
        let earlyRepaymentFrequencyMonths = 0;
        if (includeEarlyRepayment && earlyRepaymentAmount > 0) {
            switch (earlyRepaymentPeriod) {
                case 'monthly': earlyRepaymentFrequencyMonths = 1; break;
                case 'quarterly': earlyRepaymentFrequencyMonths = 3; break;
                case 'semi_annually': earlyRepaymentFrequencyMonths = 6; break;
                case 'annually': earlyRepaymentFrequencyMonths = 12; break;
                case 'once': earlyRepaymentFrequencyMonths = -1; break;
                default: earlyRepaymentFrequencyMonths = 0;
            }
        }

        // --- Цикл расчета графика платежей ---
        const maxIterations = initialLoanTermMonths * 2 + 120;

        while (currentLoanBalance > 0.005 && currentMonth < maxIterations) {
            currentMonth++;
            const loopStartDate = new Date(startDate.getFullYear(), startDate.getMonth() + currentMonth - 1);

            // Проверяем рефинансирование
            let didRefinanceThisMonth = false;
            if (includeRefinancing && currentMonth === refinancingMonth && currentAnnualRate !== refinancingRate) {
                currentAnnualRate = refinancingRate;
                didRefinanceThisMonth = true;
                remainingTermMonthsForCalc = initialLoanTermMonths - currentMonth + 1;
                if (paymentType === 'annuity' && remainingTermMonthsForCalc > 0) {
                    fixedAnnuityPayment = calculateAnnuityPayment(currentLoanBalance, remainingTermMonthsForCalc, currentAnnualRate);
                }
            }

            // Рассчитываем ежемесячные проценты
            const monthlyRate = currentAnnualRate / 12 / 100;
            let interestPayment = currentLoanBalance * monthlyRate;
            interestPayment = Math.max(0, interestPayment);

            // Рассчитываем плановое погашение основного долга и ежемесячный платеж
            let principalPaymentScheduled = 0;
            let monthlyPaymentBase = 0;

            if (paymentType === 'annuity') {
                monthlyPaymentBase = fixedAnnuityPayment;
                principalPaymentScheduled = monthlyPaymentBase - interestPayment;

                if (currentLoanBalance + interestPayment <= monthlyPaymentBase + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                    monthlyPaymentBase = currentLoanBalance + interestPayment;
                }
            } else { // Дифференцированный
                const basePrincipalPayment = initialLoanAmount / initialLoanTermMonths;
                principalPaymentScheduled = basePrincipalPayment;

                if (currentLoanBalance <= basePrincipalPayment + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                }
                monthlyPaymentBase = principalPaymentScheduled + interestPayment;
            }
            principalPaymentScheduled = Math.max(0, principalPaymentScheduled);

            // Рассчитываем страховые платежи
            let insurancePayment = 0;
            if (includeInsurance && annualInsuranceRatePercent > 0) {
                insurancePayment = currentLoanBalance * (annualInsuranceRatePercent / 100) / 12;
                insurancePayment = Math.max(0, insurancePayment);
            }

            // Учитываем другие расходы (только в первый месяц)
            const otherCostsPayment = (currentMonth === 1) ? otherCostsAmount : 0;

            // Рассчитываем досрочное погашение
            let currentEarlyRepayment = 0;
            if (includeEarlyRepayment && earlyRepaymentAmount > 0 && currentMonth >= earlyRepaymentStartMonth && currentLoanBalance > 0) {
                const isRepaymentMonth =
                    (earlyRepaymentFrequencyMonths === -1 && currentMonth === earlyRepaymentStartMonth) ||
                    (earlyRepaymentFrequencyMonths > 0 && (currentMonth - earlyRepaymentStartMonth) % earlyRepaymentFrequencyMonths === 0);

                if (isRepaymentMonth) {
                    const maxEarlyRepayment = Math.max(0, currentLoanBalance - principalPaymentScheduled);
                    currentEarlyRepayment = Math.min(earlyRepaymentAmount, maxEarlyRepayment);
                }
            }

            // Рассчитываем общую сумму платежей за месяц
            const totalPaymentThisMonth = monthlyPaymentBase + insurancePayment + otherCostsPayment + currentEarlyRepayment;

            // Обновляем итоговые суммы
            totalPrincipalPaidScheduled += principalPaymentScheduled;
            totalInterestPaid += interestPayment;
            totalInsurancePaid += insurancePayment;
            totalEarlyRepaymentPaid += currentEarlyRepayment;

            // Обновляем остаток кредита
            const startingBalance = currentLoanBalance;
            currentLoanBalance -= principalPaymentScheduled;
            currentLoanBalance -= currentEarlyRepayment;
            currentLoanBalance = Math.max(0, currentLoanBalance);

            // Пересчитываем аннуитетный платеж после досрочного погашения
            if (currentEarlyRepayment > 0 && paymentType === 'annuity' && currentLoanBalance > 0.005) {
                remainingTermMonthsForCalc = initialLoanTermMonths - currentMonth;
                if (remainingTermMonthsForCalc > 0) {
                    if (earlyRepaymentType === 'payment') {
                        fixedAnnuityPayment = calculateAnnuityPayment(currentLoanBalance, remainingTermMonthsForCalc, currentAnnualRate);
                    } else {
                        // Для "уменьшение срока" платеж остается прежним
                    }
                } else {
                    fixedAnnuityPayment = 0;
                }
            }

            // Добавляем запись в график платежей
            calculatedSchedule.push({
                month: currentMonth,
                date: loopStartDate,
                payment: monthlyPaymentBase,
                principalPayment: principalPaymentScheduled,
                interestPayment: interestPayment,
                insurancePayment: insurancePayment,
                otherCostsPayment: otherCostsPayment,
                earlyRepayment: currentEarlyRepayment,
                totalPayment: totalPaymentThisMonth,
                remainingBalance: currentLoanBalance,
                rate: currentAnnualRate,
                isRefinanceMonth: didRefinanceThisMonth
            });
        }

        if (currentMonth >= maxIterations && currentLoanBalance > 0.005) {
            console.warn("Расчет прерван из-за превышения максимального количества итераций.");
            setErrors({ general: "Расчет прерван из-за превышения максимального количества итераций. Проверьте параметры, особенно срок и досрочное погашение." });
            setResults(null);
            setSchedule([]);
            setIsCalculating(false);
            return;
        }

        const effectiveTermMonths = calculatedSchedule.length;

        // --- Готовим итоговые результаты ---
        const totalPrincipalPaid = totalPrincipalPaidScheduled + totalEarlyRepaymentPaid;
        const totalCreditCost = totalInterestPaid + totalInsurancePaid + totalOtherCostsPaid;
        const totalPaymentOverall = numInitialPayment + initialLoanAmount + totalCreditCost;

        const finalResults = {
            propertyPrice: numPropertyPrice,
            initialPayment: numInitialPayment,
            loanAmount: initialLoanAmount,
            firstMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[0].payment : 0,
            firstTotalPayment: calculatedSchedule.length > 0 ? calculatedSchedule[0].totalPayment : 0,
            lastMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].payment : 0,
            lastTotalPayment: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].totalPayment : 0,
            totalPrincipalPaid: totalPrincipalPaid,
            totalInterest: totalInterestPaid,
            totalInsurance: totalInsurancePaid,
            totalOtherCosts: totalOtherCostsPaid,
            totalEarlyRepayment: totalEarlyRepaymentPaid,
            totalCreditCost: totalCreditCost,
            totalPayment: totalPaymentOverall,
            overpaymentRatio: initialLoanAmount > 0 ? totalCreditCost / initialLoanAmount : 0,
            paybackPeriodMonths: effectiveTermMonths,
            loanToValue: numPropertyPrice > 0 ? (initialLoanAmount / numPropertyPrice) * 100 : 0,
            scheduleShort: calculatedSchedule.slice(0, 12)
        };

        setResults(finalResults);
        setSchedule(calculatedSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      propertyPrice: '6000000',
      initialPayment: '1500000',
      loanTerm: '20',
      interestRate: '7.5',
      paymentType: 'annuity',
      creditType: 'standard',
      includeEarlyRepayment: false,
      earlyRepaymentType: 'reduction',
      earlyRepaymentAmount: '100000',
      earlyRepaymentPeriod: 'monthly',
      earlyRepaymentStartMonth: '12',
      includeRefinancing: false,
      refinancingMonth: '60',
      refinancingRate: '6.0',
      includeInsurance: false,
      insuranceRate: '0.5',
      includeOtherCosts: false,
      otherCostsAmount: '10000',
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Дата', 'Платеж (Осн. долг + %)', 'Основной долг', 'Проценты'];
    if (results.totalInsurance > 0) headers.push('Страхование');
    if (results.totalOtherCosts > 0 && schedule.some(item => item.otherCostsPayment > 0)) headers.push('Прочие расходы');
    if (results.totalEarlyRepayment > 0) headers.push('Досрочное погашение');
    headers.push('Общий платеж за месяц', 'Остаток долга', 'Ставка (%)');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      const rowData = [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.payment),
        formatCsvNumber(item.principalPayment),
        formatCsvNumber(item.interestPayment),
      ];
      if (results.totalInsurance > 0) rowData.push(formatCsvNumber(item.insurancePayment));
      if (results.totalOtherCosts > 0 && schedule.some(i => i.otherCostsPayment > 0)) rowData.push(formatCsvNumber(item.otherCostsPayment));
      if (results.totalEarlyRepayment > 0) rowData.push(formatCsvNumber(item.earlyRepayment));
      rowData.push(
        formatCsvNumber(item.totalPayment),
        formatCsvNumber(item.remainingBalance),
        item.rate.toFixed(2).replace('.', ',')
      );
      return rowData.join(';');
    });

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'ипотечный_график_платежей.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePaymentSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры ипотеки
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры кредита */}
        <LoanParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Досрочное погашение */}
        <EarlyRepaymentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          earlyRepaymentPeriods={earlyRepaymentPeriods}
        />

        {/* Рефинансирование */}
        <RefinancingSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные расходы */}
        <AdditionalCostsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать ипотеку'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<RefreshCw className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;